/* eslint-disable @typescript-eslint/no-explicit-any */
import { Control, useFormState } from 'react-hook-form';
import { Fields, FormGroups } from '../types/Form';
import { renderField } from '../utils/renderField';
import { getGroupedFields } from '../utils/getGroupedFields';
import { SxProps } from '@mui/material';
import { FieldWrapper } from './FieldWrapper';

const groupSx: SxProps = {
  display: 'flex',
  gap: 2,
  flexWrap: 'wrap',
  alignItems: 'flex-end'
};

type FormFieldsProps = {
  fields: Fields<any>;
  groups?: FormGroups;
  control: Control<any>;
};

export const FormFields = ({ fields, groups, control }: FormFieldsProps) => {
  const { errors } = useFormState({ control });
  const groupedFields = getGroupedFields(fields, groups);

  const getGroupError = (
    groupFields: Record<string, any>
  ): string | undefined => {
    // Check each field in the group for errors
    for (const fieldName of Object.keys(groupFields)) {
      const fieldError = errors[fieldName];
      if (fieldError) {
        return fieldError.message as string;
      }
    }
    return undefined;
  };

  return (
    <>
      {Object.entries(groupedFields).map(([name, group]) => {
        // Skip rendering groups with no visible fields
        if (Object.keys(group.fields).length === 0) {
          return null;
        }

        const groupError = getGroupError(group.fields);

        return (
          <FieldWrapper
            dataTestId={`group-field-${name}`}
            key={name}
            label={group.label}
            labelSx={group.labelSx}
            subLabel={group.subLabel}
            subLabelSx={group.subLabelSx}
            labelError={!!groupError}
            variant={group.variant}
            sx={{
              // NOTE: Doing this to help with type inference memory issues
              ...(Object.entries(group.fields).length > 1
                ? (groupSx as any)
                : {}),
              ...(group.sx as any)
            }}
          >
            {Object.entries(group.fields).map((entry) =>
              renderField(entry, control)
            )}
          </FieldWrapper>
        );
      })}
    </>
  );
};
